<script context="module">
  import { setLocale } from '/@/multilang'
  import navigatorLanguages from 'navigator-languages'

  setLocale(navigatorLanguages())
</script>

<script>
  import { Route, router } from '/@/router.js'
  import { user, settings, connectionState } from './store.js'
  import Lazy from './Lazy.svelte'
  import Tailwindcss from './Tailwindcss.svelte'
  import * as Sentry from '@sentry/svelte'
  import UnhandledErrorApp from './UnhandledErrorApp.svelte'
  import UAParser from 'ua-parser-js'
  import { Notification } from '/@/components/Notification'
  import { recordSession } from '/@/components/webrecorder'

  export let prefix
  export let component
  export let config = '{}'
  export let context = undefined

  router.set_url_prefix(prefix)
  Object.assign(settings, {
    ...JSON.parse(config),
    context,
  })

  // eslint-disable-next-line no-undef
  if (import.meta.env.MODE === 'production') {
    // Catch uncaught exceptions
    // eslint-disable-next-line no-inner-declarations, no-unused-vars
    function onError(err) {
      // err.preventDefault?.()
      // redirect to /{prefix}/unhanlded_error ({prefix}/Content.svelte)
      window.location = router.url('/unhandled_error')
    }

    const url = new URL(settings.sentryApi)
    Sentry.init({
      dsn: `${window.location.protocol}//${url.username}@${window.location.host}/sentry${url.pathname}`,
      environment: settings.sentryEnvironment, // cc3-8ak
      release: import.meta.env.GIT_REV,
      integrations: [new Sentry.BrowserTracing()],
      autoSessionTracking: false,
    })

    Sentry.configureScope((scope) => {
      scope.setExtra('client', UAParser())
      scope.setExtra('user', {
        uuid: $user?.uuid,
        model: $user?.model,
        username: $user?.username,
        is_superuser: $user?.is_superuser,
        permissions: $user?.permissions,
      })
    })

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onError)
  }

  if (settings.recorder.includes(settings.context)) {
    window.addEventListener('beforeunload', recordSession())
  }

  // force reload when there is a new app version available
  $: router.handleReload($router)
</script>

<style lang="postcss">
  :global(html) {
    --scrollbarBG: #0001;
    --thumbBG: #2d9643;
  }
  :global(*::-webkit-scrollbar) {
    width: 6px;
  }
  :global(*) {
    --scrollbar-width: 0.5em;
    scrollbar-width: var(--scrollbar-width);
    scrollbar-color: var(--thumbBG) var(--scrollbarBG);
  }
  :global(*::-webkit-scrollbar-track) {
    background: var(--scrollbarBG);
  }
  :global(*::-webkit-scrollbar-thumb) {
    background-color: var(--thumbBG);
  }

  :global(.tinro-active) {
    @apply bg-primary-500 text-white;
  }

  :global(.scroll-shadows) {
    /* --shadows-top-offset:1.5em fits well CRUDLists and <table> in general */
    --shadows-top-offset: 0.1em;
    --table-border: 0.1em;

    --bg: #fff;
    --bgTrans: #fff0;

    --shadow: #00000019;
    --shadowTrans: #0000;

    /* 100% minus scrollbars */
    --shadowWidth: calc(100% - var(--scrollbar-width) / 4);

    background:
      /* Shadow Cover TOP */ linear-gradient(
          var(--bgTrans) var(--shadows-top-offset),
          var(--shadow) var(--shadows-top-offset),
          var(--bg) calc(var(--shadows-top-offset) + var(--table-border)),
          var(--bg) 20%,
          var(--bgTrans)
        )
        left top,
      /* Shadow Cover BOTTOM */
        linear-gradient(
          var(--bgTrans) 20%,
          var(--bg) calc(100% - var(--table-border))
        )
        left bottom,
      /* Shadow TOP */
        linear-gradient(
          var(--shadow) calc(var(--shadows-top-offset)),
          var(--shadowTrans) calc(var(--shadows-top-offset) + 1em)
        )
        left top,
      /* Shadow BOTTOM */ linear-gradient(var(--shadowTrans), var(--shadow))
        left bottom;

    background-size: var(--shadowWidth) 4em, var(--shadowWidth) 4em,
      var(--shadowWidth) calc(var(--shadows-top-offset) + 1em),
      var(--shadowWidth) 1em;

    background-attachment: local, local, scroll, scroll;

    background-repeat: no-repeat;
  }
</style>

<Tailwindcss />

{#if location.pathname === `${prefix}/unhandled_error`}
  <UnhandledErrorApp />
{:else}
  <Route path="{prefix}/*">
    {#if component == 'staff'}
      <Lazy component="{import('./staff/App.svelte')}" />
    {:else if component == 'inmate'}
      {#if context == 'inmate'}
        <Lazy component="{import('./inmate/App.svelte')}" />
      {:else}
        <Lazy component="{import('./inmate/ExternalApp.svelte')}" />
      {/if}
    {:else if component == 'visitor'}
      <Lazy component="{import('./visitor/App.svelte')}" />
    {/if}

    {#if $user.uuid}
      <Notification />
    {/if}
  </Route>
{/if}
